import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import { Button } from '@components/ui';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import PlusIcon from '@assets/icons/PlusIcon';
import { useTranslation } from '@hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { getFileUrl } from '@services/fileService';
import { deleteDocument, listDocuments } from '@services/documentVaultService';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import type { DocumentVaultDto } from '@/types/documentVault';
import type { Envelope, EnvelopeStatus } from '@workpay/ds-shared/types/envelope';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { toPaginationState } from '@utils/tabHelpers';
import { toDisplayDate } from '@utils/dateHelpers';
import { downloadAllSignedDocuments } from '@utils/downloadEnvelopeDocs';
import UploadDocumentModal from './UploadDocumentModal';
import RenameDocumentModal from './RenameDocumentModal';
import DocumentTypeBadge from './DocumentTypeBadge';
import EnvelopeStatusBadge from './EnvelopeStatusBadge';
import styles from './EmployeeDocumentsTab.module.scss';

export interface EmployeeDocumentsTabProps {
  employeeId: string;
  /** Optional — denormalised tenant organisation id. Backend accepts null. */
  organisationId?: string;
  /**
   * When true (admin surface), signing-request rows become clickable and navigate
   * to /w/signing-requests/:id. Tenant surface has no detail route, so rows are inert.
   */
  canOpenSigningRequestDetail?: boolean;
}

// Mirrors SigningRequestList.tsx — envelopes in these states are still editable.
const EDITABLE_STATUSES: EnvelopeStatus[] = [
  'DRAFT',
  'REVIEW_COMPLETED',
  'SENT',
  'VIEWED',
  'SIGNING_IN_PROGRESS',
  'REVISED',
  'PENDING_REVIEW',
  'UNDER_NEGOTIATION',
];

// DRAFT-equivalent statuses — write ops accept both, and the wizard lands on
// Place Fields (Step 4) for both.
const DRAFT_LIKE_STATUSES: EnvelopeStatus[] = ['DRAFT', 'REVIEW_COMPLETED'];

function EmployeeDocumentsTab({
  employeeId,
  organisationId,
  canOpenSigningRequestDetail = false,
}: EmployeeDocumentsTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Backend seeds MODULE_ALL_DOCUMENTS with three perms: VIEW / CREATE / DELETE.
  // There is intentionally no DOCUMENT_REQUESTS_UPDATE — rename is treated as a
  // CREATE-class write.
  const canView = hasPermission('DOCUMENT_REQUESTS_VIEW');
  const canCreate = hasPermission('DOCUMENT_REQUESTS_CREATE');
  const canDelete = hasPermission('DOCUMENT_REQUESTS_DELETE');

  // ============================================================
  // Section 1 — DOCUMENTS (uploaded files, scoped to this employee)
  // ============================================================

  const [docsPage, setDocsPage] = useState(0);
  const [docsSize, setDocsSize] = useState(10);
  const [docsSort, setDocsSort] = useState<SortState>({
    columnId: 'updatedOn',
    direction: 'desc',
  });
  const docsSortString = useMemo(
    () => `${docsSort.columnId},${docsSort.direction}`,
    [docsSort],
  );

  const {
    data: docsData,
    isLoading: docsLoading,
    isFetching: docsFetching,
    error: docsError,
  } = useQuery({
    queryKey: [
      'documents',
      'entity',
      'EMPLOYEE' as const,
      employeeId,
      { page: docsPage, size: docsSize, sort: docsSortString },
    ] as const,
    queryFn: () =>
      listDocuments(
        { page: docsPage, size: docsSize, sort: docsSortString },
        {
          filters: {
            entityType: 'EMPLOYEE',
            entityId: employeeId,
            status: 'ACTIVE',
          },
        },
      ),
    enabled: !!employeeId && canView,
  });

  const docsRows = docsData?.content ?? [];
  const docsPagination = useMemo(() => toPaginationState(docsData), [docsData]);

  const invalidateDocs = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['documents', 'entity', 'EMPLOYEE', employeeId],
      exact: false,
    });
  }, [queryClient, employeeId]);

  const [isUploadOpen, setUploadOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DocumentVaultDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentVaultDto | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => {
      invalidateDocs();
      setDeleteTarget(null);
    },
    onError: (err: unknown) => {
      const e = err as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
      setDeleteTarget(null);
    },
  });

  const handleDocDownload = useCallback(
    (doc: DocumentVaultDto) => {
      if (!doc.fileId) {
        addNotification({
          variant: 'critical',
          title: t('K_NOTIF_FAILED_DOWNLOAD_DOCUMENT', 'Cannot download — file reference missing'),
        });
        return;
      }
      window.open(getFileUrl(doc.fileId), '_blank', 'noopener,noreferrer');
    },
    [addNotification, t],
  );

  const docsRowActions = useCallback(
    (doc: DocumentVaultDto) => {
      const actions: ProfileAction[] = [
        {
          id: 'download',
          label: t('K_DOCS_ACTION_DOWNLOAD', 'Download'),
          onClick: () => handleDocDownload(doc),
        },
      ];
      if (canCreate) {
        actions.push({
          id: 'rename',
          label: t('K_DOCS_ACTION_RENAME', 'Rename'),
          onClick: () => setRenameTarget(doc),
        });
      }
      if (canDelete) {
        actions.push({
          id: 'delete',
          label: t('K_DOCS_ACTION_DELETE', 'Delete'),
          onClick: () => setDeleteTarget(doc),
        });
      }
      return <ProfileActionsMenu actions={actions} />;
    },
    [t, canCreate, canDelete, handleDocDownload],
  );

  const docsColumns = useMemo<DataTableColumn<DocumentVaultDto>[]>(
    () => [
      {
        id: 'documentName',
        header: t('K_COLUMN_DOCUMENT_NAME', 'Name'),
        sortable: true,
        sortKey: 'documentName',
        width: '280px',
        cell: (doc) => (
          <span className={styles.cellPrimary} title={doc.documentName}>
            {doc.documentName || '-'}
          </span>
        ),
      },
      {
        id: 'documentType',
        header: t('K_COLUMN_DOCUMENT_TYPE', 'Type'),
        cell: (doc) => <DocumentTypeBadge type={doc.documentType} />,
      },
      {
        id: 'updatedOn',
        header: t('K_COLUMN_UPLOADED', 'Uploaded'),
        sortable: true,
        sortKey: 'updatedOn',
        cell: (doc) => (
          <span className={styles.cellSecondary}>{toDisplayDate(doc.updatedOn)}</span>
        ),
      },
    ],
    [t],
  );

  const getDocKey = useCallback((doc: DocumentVaultDto) => doc.id, []);

  const handleDocsSort = useCallback((next: SortState | undefined) => {
    if (next) setDocsSort(next);
    setDocsPage(0);
  }, []);

  const docsEmpty = useMemo(
    () => (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyTitle}>
          {t('K_EMPTY_NO_DOCUMENTS', 'No documents yet')}
        </h3>
        <p className={styles.emptyText}>
          {canCreate
            ? t(
                'K_EMPTY_NO_DOCUMENTS_DESC',
                'Upload any paperwork that belongs on this employee record.',
              )
            : t(
                'K_EMPTY_NO_DOCUMENTS_READONLY',
                'Documents uploaded to this employee will appear here.',
              )}
        </p>
      </div>
    ),
    [t, canCreate],
  );

  // ============================================================
  // Section 2 — SIGNING REQUESTS (envelopes targeting this employee)
  // View-only: no actions, click-through to detail on admin surface.
  //
  // TODO: Backend docs (contract-service api-endpoints.md § POST /v1/envelopes/search)
  // don't whitelist `targetId`. Relying on FilterSpecification reflection to match it
  // against the entity field name. If backend rejects, add targetId to the whitelist.
  // ============================================================

  const [srPage, setSrPage] = useState(0);
  const [srSize, setSrSize] = useState(10);
  const [srSort, setSrSort] = useState<SortState>({
    columnId: 'updatedOn',
    direction: 'desc',
  });
  const srSortString = useMemo(
    () => `${srSort.columnId},${srSort.direction}`,
    [srSort],
  );

  const {
    data: srData,
    isLoading: srLoading,
    isFetching: srFetching,
    error: srError,
  } = useQuery({
    queryKey: [
      'dsEnvelopes',
      'byTarget',
      employeeId,
      { page: srPage, size: srSize, sort: srSortString },
    ] as const,
    queryFn: () =>
      dsEnvelopeService.search(
        { filters: { targetId: employeeId } },
        { page: srPage, size: srSize, sort: srSortString },
      ),
    enabled: !!employeeId,
  });

  const srRows = srData?.content ?? [];
  const srPagination = useMemo(() => toPaginationState(srData), [srData]);

  const handleSrRowClick = useCallback(
    (env: Envelope) => {
      if (!canOpenSigningRequestDetail) return;
      void navigate({
        to: '/w/signing-requests/$requestId',
        params: { requestId: env.id },
      });
    },
    [canOpenSigningRequestDetail, navigate],
  );

  // Admin-only row actions on signing requests — mirrors SigningRequestList.tsx.
  // Tenant persona sees the list but no actions (canOpenSigningRequestDetail=false).
  const [voidTarget, setVoidTarget] = useState<Envelope | null>(null);

  const invalidateEnvelopes = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['dsEnvelopes', 'byTarget', employeeId],
      exact: false,
    });
    // Also refresh any list pages / detail caches keyed under the global prefix.
    void queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'], exact: false });
  }, [queryClient, employeeId]);

  const voidMutation = useMutation({
    mutationFn: (id: string) => dsEnvelopeService.void(id, 'Voided by sender'),
    onSuccess: () => {
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_SIGNING_REQUEST_VOIDED', 'Signing request voided successfully'),
      });
      invalidateEnvelopes();
      setVoidTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
      setVoidTarget(null);
    },
  });

  const handleDownloadSigned = useCallback(
    async (env: Envelope) => {
      try {
        await downloadAllSignedDocuments(env.id, env.documents);
      } catch {
        addNotification({
          variant: 'critical',
          title: t('K_DS_NOTIF_DOWNLOAD_FAILED', 'Failed to download signed documents'),
        });
      }
    },
    [addNotification, t],
  );

  const srRowActions = useCallback(
    (env: Envelope) => {
      // Tenant persona: no actions, strictly view-only.
      if (!canOpenSigningRequestDetail) return null;

      const actions: ProfileAction[] = [];

      actions.push({
        id: 'view',
        label: t('K_VIEW', 'View'),
        onClick: () =>
          navigate({
            to: '/w/signing-requests/$requestId',
            params: { requestId: env.id },
          }),
      });

      const hasSigned = env.recipients?.some((r) => r.status === 'SIGNED');
      const isDraftLike = DRAFT_LIKE_STATUSES.includes(env.status);
      const editPerm = isDraftLike ? 'ENVELOPES_UPDATE' : 'ENVELOPES_REVISE';
      if (hasPermission(editPerm) && EDITABLE_STATUSES.includes(env.status)) {
        actions.push({
          id: 'edit',
          label: isDraftLike
            ? t('K_DS_ACTION_CONTINUE_EDITING', 'Continue Editing')
            : t('K_DS_ACTION_EDIT_AND_RESEND', 'Edit & Resend'),
          onClick: () =>
            navigate({
              to: '/w/signing-requests/$requestId/edit',
              params: { requestId: env.id },
              search: {
                retriggerMode: hasSigned ? 'continue' : 'reset_all',
                ...(isDraftLike ? { startStep: 4 } : {}),
              },
            }),
        });
      }

      if (env.status === 'COMPLETED' && hasPermission('ENVELOPES_DOWNLOAD')) {
        actions.push({
          id: 'download-signed',
          label: t('K_DS_ACTION_DOWNLOAD_SIGNED', 'Download Signed'),
          onClick: () => {
            void handleDownloadSigned(env);
          },
        });
      }

      if (
        hasPermission('ENVELOPES_VOID') &&
        env.status !== 'COMPLETED' &&
        env.status !== 'VOIDED'
      ) {
        actions.push({
          id: 'void',
          label: t('K_DS_ACTION_VOID', 'Void'),
          onClick: () => setVoidTarget(env),
        });
      }

      if (actions.length === 0) return null;
      return <ProfileActionsMenu actions={actions} />;
    },
    [canOpenSigningRequestDetail, t, navigate, hasPermission, handleDownloadSigned],
  );

  const srColumns = useMemo<DataTableColumn<Envelope>[]>(
    () => [
      {
        id: 'subject',
        header: t('K_COLUMN_SUBJECT', 'Subject'),
        sortable: true,
        sortKey: 'subject',
        width: '280px',
        cell: (env) => (
          <span className={styles.cellPrimary} title={env.subject}>
            {env.subject || '-'}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (env) => <EnvelopeStatusBadge status={env.status} />,
      },
      {
        id: 'createdOn',
        header: t('K_COLUMN_SENT', 'Sent'),
        sortable: true,
        sortKey: 'createdOn',
        cell: (env) => (
          <span className={styles.cellSecondary}>{toDisplayDate(env.createdOn)}</span>
        ),
      },
      {
        id: 'completedAt',
        header: t('K_COLUMN_COMPLETED', 'Completed'),
        sortable: true,
        sortKey: 'completedAt',
        cell: (env) => (
          <span className={styles.cellSecondary}>{toDisplayDate(env.completedAt)}</span>
        ),
      },
    ],
    [t],
  );

  const getSrKey = useCallback((env: Envelope) => env.id, []);

  const handleSrSort = useCallback((next: SortState | undefined) => {
    if (next) setSrSort(next);
    setSrPage(0);
  }, []);

  const srEmpty = useMemo(
    () => (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyTitle}>
          {t('K_EMPTY_NO_SIGNING_REQUESTS', 'No signing requests yet')}
        </h3>
        <p className={styles.emptyText}>
          {t(
            'K_EMPTY_NO_SIGNING_REQUESTS_DESC',
            'Contracts and agreements sent to this employee will appear here.',
          )}
        </p>
      </div>
    ),
    [t],
  );

  // ============================================================
  // Render
  // ============================================================

  // Forbidden (inline — sibling profile tabs + page chrome stay interactive).
  // OR over both primary queries — either section being 403 means the tab can't
  // do its job, and the user shouldn't see a half-broken surface.
  if (isForbiddenError(docsError) || isForbiddenError(srError)) {
    return <Forbidden variant="inline" hideAction />;
  }

  return (
    <div className={styles.container}>
      {/* Section 1 — Documents */}
      <section className={styles.section}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{t('K_TAB_DOCUMENTS', 'Documents')}</h2>
            <p className={styles.subtitle}>
              {t('K_DOCS_SUBTITLE', 'Files linked to this employee record.')}
            </p>
          </div>
          {canCreate && (
            <Button
              variant="filled"
              size="sm"
              leftIcon={<PlusIcon />}
              onClick={() => setUploadOpen(true)}
            >
              {t('K_DOCS_ACTION_UPLOAD', 'Upload')}
            </Button>
          )}
        </div>

        <DataTable
          columns={docsColumns}
          data={docsRows}
          rowKey={getDocKey}
          loading={docsLoading}
          fetching={docsFetching && !docsLoading}
          loadingText={t('K_LOADING_DOCUMENTS', 'Loading documents...')}
          emptyState={docsEmpty}
          sort={docsSort}
          onSortChange={handleDocsSort}
          pagination={docsPagination}
          onPageChange={setDocsPage}
          onPageSizeChange={(s) => {
            setDocsSize(s);
            setDocsPage(0);
          }}
          rowActions={docsRowActions}
          aria-label="Employee documents"
        />
      </section>

      {/* Section 2 — Signing Requests (view-only) */}
      <section className={styles.section}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {t('K_TAB_SIGNING_REQUESTS', 'Signing Requests')}
            </h2>
            <p className={styles.subtitle}>
              {t(
                'K_SIGNING_REQUESTS_SUBTITLE',
                'Contracts and agreements sent to this employee.',
              )}
            </p>
          </div>
        </div>

        <DataTable
          columns={srColumns}
          data={srRows}
          rowKey={getSrKey}
          onRowClick={canOpenSigningRequestDetail ? handleSrRowClick : undefined}
          loading={srLoading}
          fetching={srFetching && !srLoading}
          loadingText={t(
            'K_LOADING_SIGNING_REQUESTS',
            'Loading signing requests...',
          )}
          emptyState={srEmpty}
          sort={srSort}
          onSortChange={handleSrSort}
          pagination={srPagination}
          onPageChange={setSrPage}
          onPageSizeChange={(s) => {
            setSrSize(s);
            setSrPage(0);
          }}
          rowActions={canOpenSigningRequestDetail ? srRowActions : undefined}
          aria-label="Employee signing requests"
        />
      </section>

      {/* Modals */}
      <UploadDocumentModal
        isOpen={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        scope={{ entityType: 'EMPLOYEE', entityId: employeeId, organisationId }}
        onUploaded={invalidateDocs}
      />

      <RenameDocumentModal
        isOpen={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        document={renameTarget}
        onRenamed={invalidateDocs}
      />

      <AlertModal
        variant="warning"
        title={t('K_MODAL_DELETE_DOCUMENT', 'Delete Document')}
        description={t(
          'K_CONFIRM_DELETE_DOCUMENT',
          'Are you sure you want to delete "{name}"? This action can be reversed by an admin.',
        ).replace('{name}', deleteTarget?.documentName ?? '')}
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        primaryLabel={t('K_BUTTON_DELETE', 'Delete')}
        onPrimaryClick={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        loading={deleteMutation.isPending}
      />

      <AlertModal
        variant="danger"
        title={t('K_DS_MODAL_VOID_SIGNING_REQUEST', 'Void Signing Request')}
        description={t(
          'K_DS_CONFIRM_VOID_SIGNING_REQUEST',
          'Are you sure you want to void "{name}"? This action cannot be undone.',
        ).replace('{name}', voidTarget?.subject ?? '')}
        isOpen={voidTarget !== null}
        onClose={() => setVoidTarget(null)}
        primaryLabel={t('K_DS_ACTION_VOID', 'Void')}
        onPrimaryClick={() => {
          if (voidTarget) voidMutation.mutate(voidTarget.id);
        }}
        loading={voidMutation.isPending}
      />
    </div>
  );
}

export default EmployeeDocumentsTab;
